// src/intake/questions.ts
// ─────────────────────────────────────────────────────────────────────────────
// Brief 23 — Full question definitions for the FFM intake form
// ─────────────────────────────────────────────────────────────────────────────

// Loom URL constants — Peter replaces these with real embed URLs after recording
const LOOM_INCOME = 'PLACEHOLDER';
const LOOM_PENSION = 'PLACEHOLDER';
const LOOM_BUSINESS = 'PLACEHOLDER';
const LOOM_PARTNER = 'PLACEHOLDER';

export interface IntakeQuestion {
  id: string;
  type: 'text' | 'email' | 'number' | 'select' | 'textarea' | 'loom';
  label: string;
  helperText?: string;
  placeholder?: string;
  isCurrency?: boolean;
  isPercentage?: boolean;
  options?: { value: string; label: string }[];
  required?: boolean;
  showIf?: (answers: Record<string, string>) => boolean;
  loomUrl?: string;
}

export const questions: IntakeQuestion[] = [

  // ── SECTION 1: YOUR FREEDOM ─────────────────────────────────────────

  {
    id: 'targetFreedomAge',
    type: 'number',
    label: 'What age do you want to be financially free?',
    helperText: "Financial freedom means work becomes a choice, not a necessity. If you're not sure, put your best guess — we'll talk it through together.",
    placeholder: 'e.g. 55',
    required: true,
  },

  {
    id: 'clientAge',
    type: 'number',
    label: 'And how old are you now?',
    helperText: 'Your age now — not your date of birth.',
    placeholder: 'e.g. 43',
    required: true,
  },

  // ── SECTION 2: ABOUT YOU ─────────────────────────────────────────────

  {
    id: 'clientName',
    type: 'text',
    label: 'Your full name',
    placeholder: 'First and last name',
    required: true,
  },

  {
    id: 'businessName',
    type: 'text',
    label: 'Your business name',
    placeholder: 'e.g. Henderson Design Ltd',
    required: true,
  },

  {
    id: 'email',
    type: 'email',
    label: 'Your email address',
    helperText: 'Please use the same email address we have been in contact on.',
    placeholder: 'you@yourbusiness.com',
    required: true,
  },

  {
    id: 'isCouple',
    type: 'select',
    label: 'Are you planning for yourself, or with a partner?',
    options: [
      { value: 'No', label: 'Just me' },
      { value: 'Yes', label: 'Me and my partner' },
    ],
    required: true,
  },

  {
    id: 'partnerName',
    type: 'text',
    label: "Your partner's first name",
    placeholder: 'First name',
    showIf: (a) => a.isCouple === 'Yes',
  },

  // ── LOOM: INCOME EXPLAINER ───────────────────────────────────────────

  {
    id: 'loom_income',
    type: 'loom',
    label: 'Before we look at income — a quick explainer',
    loomUrl: LOOM_INCOME,
  },

  // ── SECTION 3: YOUR INCOME ───────────────────────────────────────────

  {
    id: 'annualSalary',
    type: 'number',
    isCurrency: true,
    label: 'Annual salary from your company',
    helperText: 'Your director salary — typically £12,570. Enter 0 if you do not take a salary.',
    placeholder: '12570',
  },

  {
    id: 'annualDividends',
    type: 'number',
    isCurrency: true,
    label: 'Annual dividends drawn this year',
    helperText: 'Total dividends from your company in the current tax year (April to April). Estimate is fine.',
    placeholder: '0',
  },

  {
    id: 'otherIncome',
    type: 'number',
    isCurrency: true,
    label: 'Any other income?',
    helperText: 'Employment income, freelance, or anything else received personally — not through your company. Enter 0 if none.',
    placeholder: '0',
  },

  {
    id: 'monthlyHMRC',
    type: 'number',
    isCurrency: true,
    label: 'Do you pay your tax bill by monthly standing order?',
    helperText: 'If yes, enter the monthly amount. Enter 0 if you pay via self-assessment in January and July.',
    placeholder: '0',
  },

  // ── LOOM: PENSION EXPLAINER ──────────────────────────────────────────

  {
    id: 'loom_pension',
    type: 'loom',
    label: 'Before your pension questions — this one matters',
    loomUrl: LOOM_PENSION,
  },

  // ── SECTION 4: YOUR PENSION ──────────────────────────────────────────

  {
    id: 'dcPensionTotal',
    type: 'number',
    isCurrency: true,
    label: 'Approximate total value of your pension pot(s)',
    helperText: "Combined value of all defined contribution pensions — old workplace pensions, SIPPs, personal pensions. Check your most recent statements. Don't include any final salary pensions here.",
    placeholder: '0',
  },

  {
    id: 'pensionCompanyFunded',
    type: 'number',
    isCurrency: true,
    label: 'How much does your company pay into your pension each month?',
    helperText: "The company's contribution — not yours. Enter 0 if the company does not currently contribute.",
    placeholder: '0',
  },

  {
    id: 'hasDBPension',
    type: 'select',
    label: 'Do you have a defined benefit (final salary) pension?',
    helperText: 'A defined benefit pension promises a specific annual income in retirement — usually from a previous employer, public sector, or NHS role.',
    options: [
      { value: 'No', label: 'No' },
      { value: 'Yes', label: 'Yes' },
    ],
  },

  {
    id: 'dbSchemeName',
    type: 'text',
    label: 'What is the name of the scheme?',
    helperText: 'e.g. NHS Pension, Local Government Pension Scheme, BT Pension Scheme',
    placeholder: 'Scheme name',
    showIf: (a) => a.hasDBPension === 'Yes',
  },

  {
    id: 'annualDBPension',
    type: 'number',
    isCurrency: true,
    label: 'Annual income this pension will pay at retirement',
    helperText: 'From your most recent annual statement. Estimate is fine.',
    placeholder: '0',
    showIf: (a) => a.hasDBPension === 'Yes',
  },

  {
    id: 'dbNRA',
    type: 'number',
    label: 'Normal retirement age for this scheme',
    helperText: 'The age at which this pension becomes payable without reduction — shown on your scheme statement. Typically 60, 65, or 67.',
    placeholder: '65',
    showIf: (a) => a.hasDBPension === 'Yes',
  },

  // ── SECTION 5: YOUR SAVINGS ──────────────────────────────────────────

  {
    id: 'cashISABalance',
    type: 'number',
    isCurrency: true,
    label: 'Cash ISA total',
    helperText: 'Current value of any cash ISAs. Enter 0 if none.',
    placeholder: '0',
  },

  {
    id: 'stocksSharesISABalance',
    type: 'number',
    isCurrency: true,
    label: 'Stocks and shares ISA total',
    helperText: 'Current value of any stocks and shares ISAs. Check your provider app or most recent statement. Enter 0 if none.',
    placeholder: '0',
  },

  {
    id: 'efAccount1Balance',
    type: 'number',
    isCurrency: true,
    label: 'Emergency and accessible savings',
    helperText: 'Total cash in current accounts, easy-access savings, or any financial buffer. Not your ISAs — those were above. Rough figure is fine.',
    placeholder: '0',
  },

  // ── SECTION 6: YOUR HOME ─────────────────────────────────────────────

  {
    id: 'homeValue',
    type: 'number',
    isCurrency: true,
    label: 'Estimated value of your home today',
    helperText: 'Check Zoopla or Rightmove for similar properties if unsure. Enter 0 if you rent.',
    placeholder: '0',
  },

  {
    id: 'mortgageBalance',
    type: 'number',
    isCurrency: true,
    label: 'Outstanding mortgage balance',
    helperText: 'Check your lender app or most recent statement. Enter 0 if mortgage-free or renting.',
    placeholder: '0',
  },

  {
    id: 'monthlyMortgagePayment',
    type: 'number',
    isCurrency: true,
    label: 'Monthly mortgage payment',
    helperText: 'Capital and interest combined. Enter 0 if mortgage-free or renting.',
    placeholder: '0',
    showIf: (a) => parseInt(a.mortgageBalance || '0') > 0,
  },

  // ── SECTION 7A: CONSUMER DEBT (GATED) ───────────────────────────────

  {
    id: 'hasDebt',
    type: 'select',
    label: 'Do you have any outstanding personal loans, credit cards, or finance agreements?',
    options: [
      { value: 'No', label: 'No' },
      { value: 'Yes', label: 'Yes' },
    ],
  },

  {
    id: 'consumerDebtTotal',
    type: 'number',
    isCurrency: true,
    label: 'Approximate total of personal debt',
    helperText: 'Credit cards, personal loans, car finance — combined. Rough figure is fine.',
    placeholder: '0',
    showIf: (a) => a.hasDebt === 'Yes',
  },

  // ── SECTION 7B: RENTAL PROPERTY (GATED) ─────────────────────────────

  {
    id: 'hasRental',
    type: 'select',
    label: 'Do you own any rental properties?',
    options: [
      { value: 'No', label: 'No' },
      { value: 'Yes', label: 'Yes' },
    ],
  },

  {
    id: 'rentalPropertyValue',
    type: 'number',
    isCurrency: true,
    label: 'Approximate combined value of rental properties',
    placeholder: '0',
    showIf: (a) => a.hasRental === 'Yes',
  },

  {
    id: 'rentalMortgageBalance',
    type: 'number',
    isCurrency: true,
    label: 'Outstanding mortgage(s) on rental properties',
    helperText: 'Combined. Enter 0 if mortgage-free.',
    placeholder: '0',
    showIf: (a) => a.hasRental === 'Yes',
  },

  {
    id: 'monthlyRentalIncome',
    type: 'number',
    isCurrency: true,
    label: 'Monthly rental income (before costs)',
    helperText: 'Total rent received across all properties before expenses.',
    placeholder: '0',
    showIf: (a) => a.hasRental === 'Yes',
  },

  {
    id: 'monthlyRentalCosts',
    type: 'number',
    isCurrency: true,
    label: 'Monthly running costs on rental properties',
    helperText: 'Management fees, maintenance, insurance, letting agent fees — combined.',
    placeholder: '0',
    showIf: (a) => a.hasRental === 'Yes',
  },

  // ── SECTION 7C: CHILDREN'S COSTS (GATED) ────────────────────────────

  {
    id: 'hasChildCosts',
    type: 'select',
    label: 'Do you have regular financial commitments for children?',
    helperText: 'School fees, university support, regular financial help for a child.',
    options: [
      { value: 'No', label: 'No' },
      { value: 'Yes', label: 'Yes' },
    ],
  },

  {
    id: 'monthlyChildCosts',
    type: 'number',
    isCurrency: true,
    label: "Monthly amount committed to children's costs",
    helperText: 'School fees, university support — combined monthly total.',
    placeholder: '0',
    showIf: (a) => a.hasChildCosts === 'Yes',
  },

  {
    id: 'childCostsEndAge',
    type: 'number',
    label: 'Your age when these costs are expected to stop',
    helperText: "Your age — not your child's. E.g. if costs end in 4 years and you're 43, enter 47.",
    placeholder: '0',
    showIf: (a) => a.hasChildCosts === 'Yes',
  },

  // ── LOOM: BUSINESS EXPLAINER ─────────────────────────────────────────

  {
    id: 'loom_business',
    type: 'loom',
    label: 'Almost done — just your business picture',
    loomUrl: LOOM_BUSINESS,
  },

  // ── SECTION 8: YOUR BUSINESS ─────────────────────────────────────────

  {
    id: 'businessOperatingBuffer',
    type: 'number',
    isCurrency: true,
    label: 'Business cash buffer above day-to-day needs',
    helperText: "How much cash does the business currently hold above what it needs to operate normally? Separate from any tax reserves. Rough figure is fine.",
    placeholder: '0',
  },

  {
    id: 'businessTaxReserve',
    type: 'number',
    isCurrency: true,
    label: 'Tax reserves held in the business',
    helperText: 'Any amounts set aside for corporation tax, VAT, or PAYE. Enter 0 if not held separately.',
    placeholder: '0',
  },

  {
    id: 'businessCTEstimate',
    type: 'number',
    isCurrency: true,
    label: 'Rough corporation tax estimate for this year',
    helperText: "Your accountant will know this — leave blank if you're not sure. We'll confirm in the session.",
    placeholder: '0',
  },

  {
    id: 'directorLoanBalance',
    type: 'number',
    isCurrency: true,
    label: "Director's loan account balance",
    helperText: 'Positive if the company owes you money. Negative (e.g. -8000) if you owe the company. Enter 0 if no DLA.',
    placeholder: '0',
  },

  // ── SECTION 9: PARTNER (COUPLE ONLY) ────────────────────────────────

  {
    id: 'loom_partner',
    type: 'loom',
    label: "Now a few questions about your partner",
    loomUrl: LOOM_PARTNER,
    showIf: (a) => a.isCouple === 'Yes',
  },

  {
    id: 'partnerAge',
    type: 'number',
    label: "Your partner's age",
    placeholder: 'e.g. 41',
    showIf: (a) => a.isCouple === 'Yes',
  },

  {
    id: 'partnerSalary',
    type: 'number',
    isCurrency: true,
    label: "Your partner's annual salary",
    helperText: "Director salary or PAYE salary. Enter 0 if they don't take a salary.",
    placeholder: '0',
    showIf: (a) => a.isCouple === 'Yes',
  },

  {
    id: 'partnerDividends',
    type: 'number',
    isCurrency: true,
    label: "Your partner's annual dividends",
    helperText: 'Dividends from their company this tax year. Enter 0 if none.',
    placeholder: '0',
    showIf: (a) => a.isCouple === 'Yes',
  },

  {
    id: 'partnerOtherIncome',
    type: 'number',
    isCurrency: true,
    label: "Any other income for your partner?",
    helperText: 'Employment, freelance, or other personal income. Enter 0 if none.',
    placeholder: '0',
    showIf: (a) => a.isCouple === 'Yes',
  },

  {
    id: 'partnerDCPension',
    type: 'number',
    isCurrency: true,
    label: "Your partner's pension pot total",
    helperText: "Combined value of all their defined contribution pensions. Estimate fine. Don't include any final salary pensions.",
    placeholder: '0',
    showIf: (a) => a.isCouple === 'Yes',
  },

  {
    id: 'partnerEmployerPension',
    type: 'number',
    isCurrency: true,
    label: "Monthly employer pension contribution for your partner",
    helperText: "The company's contribution into their pension monthly. Enter 0 if none.",
    placeholder: '0',
    showIf: (a) => a.isCouple === 'Yes',
  },

  {
    id: 'partnerHasDB',
    type: 'select',
    label: 'Does your partner have a defined benefit (final salary) pension?',
    options: [
      { value: 'No', label: 'No' },
      { value: 'Yes', label: 'Yes' },
    ],
    showIf: (a) => a.isCouple === 'Yes',
  },

  {
    id: 'partnerDBScheme',
    type: 'text',
    label: "Partner's DB scheme name",
    placeholder: 'e.g. NHS Pension',
    showIf: (a) => a.isCouple === 'Yes' && a.partnerHasDB === 'Yes',
  },

  {
    id: 'partnerAnnualDBPension',
    type: 'number',
    isCurrency: true,
    label: "Annual income from your partner's DB pension at retirement",
    placeholder: '0',
    showIf: (a) => a.isCouple === 'Yes' && a.partnerHasDB === 'Yes',
  },

  {
    id: 'partnerDBNRA',
    type: 'number',
    label: "Normal retirement age for your partner's DB scheme",
    placeholder: '65',
    showIf: (a) => a.isCouple === 'Yes' && a.partnerHasDB === 'Yes',
  },

  {
    id: 'partnerCashISA',
    type: 'number',
    isCurrency: true,
    label: "Your partner's cash ISA total",
    helperText: 'Enter 0 if none.',
    placeholder: '0',
    showIf: (a) => a.isCouple === 'Yes',
  },

  {
    id: 'partnerStocksSharesISA',
    type: 'number',
    isCurrency: true,
    label: "Your partner's stocks and shares ISA total",
    helperText: 'Enter 0 if none.',
    placeholder: '0',
    showIf: (a) => a.isCouple === 'Yes',
  },

  // ── SECTION 10: BEFORE WE MEET ───────────────────────────────────────

  {
    id: 'sessionNotes',
    type: 'textarea',
    label: 'Is there anything specific you would like to focus on in your session?',
    helperText: 'No pressure — completely optional. But anything you share here shapes how I prepare.',
    placeholder: 'e.g. We are thinking about buying a second property. Or: I have no idea where to start with pensions.',
  },

];
